import { NavLink, Outlet, useNavigate, useOutletContext } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Wine, 
  LayoutDashboard, 
  Upload, 
  Users, 
  ClipboardList, 
  History,
  Trophy,
  Swords,
  LogOut,
  BookOpen,
  Target,
  Library,
  Layers,
  Grape,
  UtensilsCrossed,
  Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import UserProfilePopup from "@/components/UserProfilePopup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const adminNavItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/flashspecs", label: "FlashSpecs", icon: Layers },
  { path: "/cocktails", label: "Cocktails", icon: Wine },
  { path: "/upload", label: "Upload Specs", icon: Upload },
  { path: "/staff", label: "Team", icon: Users },
  { path: "/create-test", label: "Create Test", icon: ClipboardList },
  { path: "/library", label: "Library", icon: Library },
  { path: "/wines", label: "Wines", icon: Grape },
  { path: "/food", label: "Food", icon: UtensilsCrossed },
  { path: "/competitions", label: "Head-to-Head", icon: Swords },
  { path: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { path: "/history", label: "Test History", icon: History },
];

const userNavItems = [
  { path: "/dashboard", label: "My Dashboard", icon: LayoutDashboard },
  { path: "/flashspecs", label: "FlashSpecs", icon: Layers },
  { path: "/leaderboard", label: "Leaderboard", icon: Trophy },
];

export default function Layout() {
  const navigate = useNavigate();
  const context = useOutletContext();
  const user = context?.user;
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileExtras, setProfileExtras] = useState({ org_name: null, venue_name: null });

  // Hydrate org/venue names for the profile popup (only when relevant + not yet loaded)
  useEffect(() => {
    if (!user?.org_id) return;
    if (profileExtras.org_name) return;
    axios.get(`${API}/org/my-org`).then(res => {
      const venue = (res.data.venues || []).find(v => v.id === user.venue_id);
      setProfileExtras({ org_name: res.data.org?.name || null, venue_name: venue?.name || null });
    }).catch(() => {});
  }, [user?.org_id, user?.venue_id, profileExtras.org_name]);

  const isAdmin = user?.role === "admin" || user?.role === "org_admin" || user?.role === "venue_manager" || user?.role === "system_admin";
  const isSystemAdmin = user?.role === "system_admin";
  const canEditCocktails = user?.role === "admin" || user?.role === "org_admin" || user?.role === "system_admin";
  let navItems = isAdmin
    ? adminNavItems.filter(item => canEditCocktails || item.path !== "/upload")
    : userNavItems;
  if (isSystemAdmin) {
    navItems = [
      ...navItems,
      { path: "/admin/gamification", label: "Gamification", icon: Award },
    ];
  }

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // logout is a best-effort cleanup; clearing local token below is what actually matters
    }
    localStorage.removeItem("session_token");
    toast.success("Logged out");
    navigate("/login");
  };

  return (
    <div className="min-h-screen">
      {/* Desktop Sidebar */}
      <nav className="sidebar-nav" data-testid="sidebar-nav">
        <div className="mb-8">
          <h1 className="text-2xl font-bold tracking-tight text-[hsl(var(--primary))]">
            FlashSpecs
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Cocktail Testing System
          </p>
        </div>
        
        <div className="flex flex-col gap-1 flex-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[hsl(var(--primary)/.15)] text-[hsl(var(--primary))] border-l-2 border-[hsl(var(--primary))]"
                    : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]"
                }`
              }
            >
              <item.icon size={20} />
              {item.label}
            </NavLink>
          ))}
        </div>
        
        {/* User Info & Logout */}
        <div className="pt-4 border-t border-[hsl(var(--border))] space-y-3">
          {user && (
            <button
              type="button"
              onClick={() => setProfileOpen(true)}
              className="w-full flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-[hsl(var(--secondary))] transition-colors text-left"
              data-testid="user-info-trigger"
              aria-label="Open profile"
            >
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center">
                  <span className="text-[hsl(var(--primary))] text-sm font-semibold">
                    {user.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                  {isAdmin ? "Admin" : user.email || "Staff"}
                </p>
              </div>
            </button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="w-full justify-start text-[hsl(var(--muted-foreground))] hover:text-red-400"
            data-testid="logout-btn"
          >
            <LogOut size={18} className="mr-2" />
            Sign Out
          </Button>
        </div>
      </nav>

      {/* Mobile Bottom Nav */}
      <nav className="mobile-nav" data-testid="mobile-nav">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg transition-colors shrink-0 min-w-[44px] min-h-[44px] justify-center ${
                isActive
                  ? "text-[hsl(var(--primary))] bg-[hsl(var(--primary)/.12)] font-bold"
                  : "text-[hsl(var(--muted-foreground))]"
              }`
            }
          >
            {/* Active state is never colour-only: the pill background + bold
                label above provide a shape/weight cue too (desktop sidebar
                already has its own border-l-2 cue for the same reason). */}
            <item.icon size={20} />
            <span className="text-[9px] leading-tight">{item.label.split(' ')[0]}</span>
          </NavLink>
        ))}
        {/* Spacer for Made with Emergent badge */}
        <div className="shrink-0 w-40" aria-hidden="true" />
      </nav>

      {/* Main Content */}
      <main className="main-content" data-testid="main-content">
        <Outlet context={{ user }} />
      </main>

      {/* User profile + invite-code redemption popup */}
      <UserProfilePopup
        user={user ? { ...user, ...profileExtras } : null}
        open={profileOpen}
        onOpenChange={setProfileOpen}
        onJoined={() => window.location.reload()}
      />
    </div>
  );
}
